import axios from '../utils/axios';

/**
 * Session-level scan of uploaded Nimbus episode .db files (Options →
 * Nimbus Recordings → "Scan recordings"). The server opens each
 * episode's uploaded whole-.db, compares it against the blob chunks it
 * received live, cross-checks the .db runs table against RunData, and
 * flags windows where at least two of wheel speed / dynamic pressure /
 * power / GPS move together with no run covering them.
 */

export interface ScanTableInfo {
  db_rows: number;
  db_ts_min_ms: number | null;
  db_ts_max_ms: number | null;
  server_rows: number;
  server_chunks: number;
  server_ts_min_ms: number | null;
  server_ts_max_ms: number | null;
  db_has_more: boolean;
}

export interface ScanRunInfo {
  run_uuid: string;
  run_number: number | null;
  description: string;
  start_ts_ms: number | null;
  end_ts_ms: number | null;
  partial: boolean;
  // false = the run exists only as server RunData (e.g. a scan-recovered
  // window) with no .db runs entry — re-import does not apply to it.
  in_db: boolean;
  on_server: boolean;
  rundata_id: string | null;
  run_status: number | null;
  // The last stream build's failure for this run, if any
  // (rundata.stream_build.error).
  build_error: string | null;
}

export interface MissingRunCandidate {
  start_ts_ms: number;
  end_ts_ms: number;
  duration_s: number;
  channels: string[];
}

/** One uploaded .db file, as its bucket key describes it. */
export interface EpisodeFileInfo {
  filename: string;
  s3_key: string;
  captured_ts: string;
  device_id: string | null;
}

export interface EpisodeScan {
  episode_id: string;
  device_id: string | null;
  start_ts_ms: number;
  end_ts_ms: number | null;
  db_upload_state: string | null;
  db_backup_verified_at: string | null;
  file: EpisodeFileInfo | null;
  reason?: string;
  error?: string;
  tables?: Record<string, ScanTableInfo>;
  runs?: ScanRunInfo[];
  missing_run_candidates?: MissingRunCandidate[];
  update_available?: boolean;
}

export interface SessionEpisodeScan {
  session_id: string;
  episodes: EpisodeScan[];
}

/** Scan every episode of the session. Slow (may download .db files from S3). */
export async function scanSessionEpisodes(sessionId: string): Promise<SessionEpisodeScan> {
  return axios.post(`/episode-db-scan/session/${sessionId}`).then((res) => res.data);
}

/**
 * "Update blob info": dispatch the idempotent .db ingest for one uploaded
 * file — rebuilds run zips whose coverage grew and creates RunData rows
 * for runs the server never saw. Runs in the background server-side.
 */
export async function ingestEpisodeDb(filename: string): Promise<{ status: string; filename: string }> {
  return axios.post('/episode-dbs-s3/ingest', { filename }).then((res) => res.data);
}

export interface RecoveryResult {
  outcome: 'created' | 'attached' | 'exists' | 'rebuilt' | 'adopted' | 'skipped';
  rundata_id?: string;
  config_id?: string | null;
  // {table_name: rows inserted into the chunk store from the .db}
  backfilled: Record<string, number>;
}

/**
 * "Add run": recover a potential-missing-run window as a RunData built
 * from the .db — attached to the chosen session config, or unattached
 * when no configId is given. Idempotent on the artifact — the run UUID
 * derives from the window, so repeating finds the existing row — but NOT
 * on the attachment: repeating with a configId is how a run recovered
 * unattached gets placed, and that returns 'attached' rather than
 * 'exists'.
 */
export async function addMissingRun(
  episodeId: string,
  startTsMs: number,
  endTsMs: number,
  configId?: string,
): Promise<RecoveryResult> {
  return axios
    .post(`/episode-db-scan/episode/${episodeId}/add-run`, {
      start_ts_ms: startTsMs,
      end_ts_ms: endTsMs,
      ...(configId ? { config_id: configId } : {}),
    })
    .then((res) => res.data);
}

/**
 * "Re-import": backfill the run window's missing rows into the chunk
 * store, force-rebuild the run's artifact from the .db, and reprocess it
 * if it was already processed.
 */
export async function reimportRun(episodeId: string, runUuid: string): Promise<RecoveryResult> {
  return axios
    .post(`/episode-db-scan/episode/${episodeId}/reimport-run`, { run_uuid: runUuid })
    .then((res) => res.data);
}

/**
 * The recordings page (Account → Recordings) starts from a file rather than
 * from a session: list the team's uploaded .db files, open one, and see which
 * of its runs the server has. A run that is missing — or that is on the server
 * but sits in no session — can be imported into any config of any session.
 */

export interface FileScanRun extends ScanRunInfo {
  // A row for this run existed and has been deleted; on_server is then false.
  deleted_on_server: boolean;
  // The server would take this run as an import: it is the caller's, it sits
  // in no config, and the file holding its episode's data can be read.
  importable: boolean;
  // Where the run's row sits. All four are null for a run that is on the
  // server in no config, and for a run of another team's episode.
  config_id: string | null;
  config_letter_number: number | null;
  session_id: string | null;
  session_description: string | null;
  // Server runs with no phone run id (device zips) that start near this run,
  // nearest first — what the page offers to link it to. Empty when the server
  // already has a row for the run.
  link_candidates: LinkCandidate[];
}

/** A server run that may be a phone run under another name: a zip carries no phone run id. */
export interface LinkCandidate {
  rundata_id: string;
  description: string;
  // Server run start minus phone run start, in seconds.
  offset_s: number;
  config_letter_number: number | null;
  session_id: string | null;
  session_description: string | null;
}

// Whose an episode found in a file is. Only 'mine' is ever sent to a caller
// who is not an admin, and only 'mine' can be imported from, by anyone.
export type EpisodeOwnership = 'mine' | 'other_team' | 'unregistered';

// The file an episode's sensor data is in. A recording lists the runs of every
// earlier recording on the phone and holds the data of its own alone, so this
// is often not the file that was opened — and it is the one an import reads.
export interface EpisodeDataFile {
  filename: string;
  s3_key: string;
  is_this_file: boolean;
}

export interface FileScanEpisode {
  episode_id: string;
  device_id: string | null;
  start_ts_ms: number | null;
  end_ts_ms: number | null;
  ownership: EpisodeOwnership;
  // Set for 'other_team' only.
  team_name: string | null;
  // The session the phone was pointed at, for the caller's own episodes.
  testsession_id: string | null;
  // Null when the opened file does not say where the episode was recorded.
  data_file: EpisodeDataFile | null;
  // It can be linked to a session from `data_file`: the caller's own episode,
  // or, for an admin, one the phone never registered (linking claims it).
  linkable: boolean;
  runs: FileScanRun[];
}

export interface EpisodeFileScan {
  file: EpisodeFileInfo;
  episodes: FileScanEpisode[];
}

/** The caller's team's uploaded .db files captured in [from, to] — up to `to`, without `from` — newest first. */
export async function listEpisodeFiles(from: string | undefined, to: string): Promise<EpisodeFileInfo[]> {
  return axios.get('/episode-db-scan/files', { params: from ? { from, to } : { to } }).then((res) => res.data);
}

/** Cross-check one file's runs against the server. Slow the first time (downloads the .db). */
export async function scanEpisodeFile(s3Key: string): Promise<EpisodeFileScan> {
  return axios.post('/episode-db-scan/file', { s3_key: s3Key }).then((res) => res.data);
}

/**
 * Import one run from the file into a config. 'created' for a run the server
 * did not have, 'attached' for one it had in no config, 'exists' for one that
 * already sits in a config — which is left where it is, and `config_id` in the
 * result says where that is.
 *
 * `s3Key` is the file the run's data is in — its episode's `data_file` — which
 * need not be the file the run was found listed in.
 */
export async function importEpisodeRun(
  s3Key: string,
  episodeId: string,
  runUuid: string,
  configId: string,
): Promise<RecoveryResult> {
  return axios
    .post('/episode-db-scan/file/import-run', {
      s3_key: s3Key,
      episode_id: episodeId,
      run_uuid: runUuid,
      config_id: configId,
    })
    .then((res) => res.data);
}

export interface LinkEpisodeResult {
  episode_id: string;
  testsession_id: string;
  session_description: string;
  // True when the server made the episode's row: the phone never registered it.
  claimed: boolean;
  // The recording's rows are stored in the background.
  ingest: 'dispatched' | 'already_running';
}

/**
 * Link one episode to a session of the caller's team and store its rows. The
 * session is how its runs find the episode's BLE frames when they are
 * reprocessed. `s3Key` is the episode's `data_file`.
 */
export async function linkEpisodeToSession(
  s3Key: string,
  episodeId: string,
  sessionId: string,
): Promise<LinkEpisodeResult> {
  return axios
    .post('/episode-db-scan/file/link-episode', { s3_key: s3Key, episode_id: episodeId, session_id: sessionId })
    .then((res) => res.data);
}

/** Mark a server run (one of the run's `link_candidates`) as this phone run. */
export async function linkRunToServerRun(
  s3Key: string,
  episodeId: string,
  runUuid: string,
  rundataId: string,
): Promise<{ rundata_id: string; run_uuid: string; outcome: 'linked' | 'already_linked' }> {
  return axios
    .post('/episode-db-scan/file/link-run', {
      s3_key: s3Key,
      episode_id: episodeId,
      run_uuid: runUuid,
      rundata_id: rundataId,
    })
    .then((res) => res.data);
}
